import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  X, 
  ChevronLeft, 
  ChevronRight,
  CheckCircle2,
  XCircle,
  MessageSquare,
  Filter
} from 'lucide-react';

const STATUS_STYLES = {
  'Draft': 'status-badge status-draft',
  'For validation': 'status-badge status-validation',
  'Validated': 'status-badge status-validated',
  'Deferred': 'status-badge status-deferred',
  'Archived': 'status-badge status-archived'
};

const MeetingModePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { api, canValidate, user, isAuthenticated, loading: authLoading } = useAuth();
  
  const [decisions, setDecisions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionNote, setActionNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCategories();
      fetchDecisions();
    }
  }, [selectedCategory, isAuthenticated]);

  useEffect(() => {
    const decisionParam = searchParams.get('decision');
    if (decisionParam && decisions.length > 0) {
      const index = decisions.findIndex(d => d.decision_id === decisionParam);
      if (index >= 0) {
        setCurrentIndex(index);
      }
    }
  }, [searchParams, decisions]);

  const fetchCategories = async () => {
    try {
      const response = await api().get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchDecisions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('status', 'For validation');
      if (selectedCategory) {
        params.append('category', selectedCategory);
      }
      const response = await api().get(`/decisions?${params.toString()}`);
      setDecisions(response.data);
    } catch (error) {
      console.error('Failed to fetch decisions:', error);
    } finally {
      setLoading(false);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < decisions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const exitMeetingMode = () => {
    navigate('/decisions');
  };

  const openActionModal = (type) => {
    setActionType(type);
    setActionNote('');
    setShowActionModal(true);
  };

  const closeActionModal = () => {
    setShowActionModal(false);
    setActionType('');
    setActionNote('');
  };

  const handleQuickAction = async () => {
    if (!actionType) return;
    
    setSubmitting(true);
    const decision = decisions[currentIndex];
    
    try {
      await api().post(`/decisions/${decision.decision_id}/status`, {
        new_status: actionType,
        note: actionNote
      });
      
      toast.success(`Decision ${actionType.toLowerCase()} successfully`);
      closeActionModal();
      await fetchDecisions();
      
      if (currentIndex >= decisions.length - 1) {
        setCurrentIndex(Math.max(0, decisions.length - 2));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  const decision = decisions[currentIndex];

  if (authLoading || loading) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100
      }}>
        <div style={{
          width: 48,
          height: 48,
          border: '4px solid #e5e7eb',
          borderTopColor: '#16a34a',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'white',
      zIndex: 100,
      overflowY: 'auto'
    }}>
      {/* Top Bar */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: '#0a2e0a',
        color: 'white',
        padding: '12px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 110
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <h1 style={{ fontSize: 18, fontWeight: 'bold' }}>Meeting Mode</h1>
          <Badge variant="secondary" className="bg-white/20 text-white">
            {decisions.length} decisions for validation
          </Badge>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Button
            variant="ghost"
            size="sm"
            style={{ color: 'white' }}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </Button>
          <Button
            variant="ghost"
            size="sm"
            style={{ color: 'white' }}
            onClick={exitMeetingMode}
          >
            <X className="w-4 h-4 mr-2" />
            Exit
          </Button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div style={{
          position: 'fixed',
          top: 56,
          left: 0,
          right: 0,
          backgroundColor: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '16px 24px',
          zIndex: 105
        }}>
          <div style={{ maxWidth: 768, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 16 }}>
            <Label>Category:</Label>
            <Select value={selectedCategory || "all"} onValueChange={(v) => setSelectedCategory(v === "all" ? "" : v)}>
              <SelectTrigger style={{ width: 300 }}>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Main Content */}
      {decisions.length === 0 ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          paddingTop: 80
        }}>
          <CheckCircle2 style={{ width: 64, height: 64, color: '#22c55e', marginBottom: 16 }} />
          <h2 style={{ fontSize: 24, fontWeight: 'bold', marginBottom: 8 }}>All Caught Up!</h2>
          <p style={{ color: '#6b7280' }}>No decisions pending validation</p>
          <Button onClick={exitMeetingMode} style={{ marginTop: 16 }}>
            Return to Library
          </Button>
        </div>
      ) : (
        <div style={{
          maxWidth: 1024,
          margin: '0 auto',
          padding: '100px 32px 120px 32px'
        }}>
          {/* Progress */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
            <span style={{ fontSize: 14, color: '#6b7280' }}>
              Decision {currentIndex + 1} of {decisions.length}
            </span>
            <div style={{ width: 192, height: 8, backgroundColor: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
              <div 
                style={{
                  height: '100%',
                  backgroundColor: '#16a34a',
                  width: `${((currentIndex + 1) / decisions.length) * 100}%`,
                  transition: 'width 0.3s'
                }}
              ></div>
            </div>
          </div>

          {/* Decision Content */}
          {decision && (
            <div>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 32 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <h1 style={{ fontSize: 36, fontWeight: 'bold' }}>{decision.decision_id}</h1>
                    <span className={STATUS_STYLES[decision.status]}>{decision.status}</span>
                  </div>
                  <p style={{ fontSize: 18, color: '#6b7280' }}>{decision.category}</p>
                </div>
                {decision.twg_view === 'Yes' && (
                  <Badge style={{ backgroundColor: '#fef3c7', color: '#92400e', fontSize: 16, padding: '4px 16px' }}>
                    TWG Priority
                  </Badge>
                )}
              </div>

              {/* Decision Question */}
              <div style={{ backgroundColor: '#f0fdf4', borderLeft: '4px solid #16a34a', padding: 24, borderRadius: '0 8px 8px 0', marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>Decision Question</h2>
                <p style={{ fontSize: 18, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{decision.decision_question || 'Not specified'}</p>
              </div>

              {/* Decision Statement */}
              <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>Decision Statement</h2>
                <p style={{ fontSize: 18, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{decision.decision_statement || 'Not specified'}</p>
              </div>

              {/* Strategic Context */}
              {decision.strategic_context && (
                <div style={{ marginBottom: 24 }}>
                  <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>Strategic Context</h2>
                  <p style={{ fontSize: 16, lineHeight: 1.6, color: '#6b7280', whiteSpace: 'pre-wrap' }}>{decision.strategic_context}</p>
                </div>
              )}

              {/* Options / Trade-offs */}
              {decision.options_tradeoffs && (
                <div style={{ backgroundColor: '#f9fafb', padding: 24, borderRadius: 8, marginBottom: 24 }}>
                  <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>Options / Trade-offs</h2>
                  <p style={{ fontSize: 16, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{decision.options_tradeoffs}</p>
                </div>
              )}

              {/* Recommended Option */}
              {decision.recommended_option && (
                <div style={{ backgroundColor: '#f0fdf4', borderLeft: '4px solid #22c55e', padding: 24, borderRadius: '0 8px 8px 0', marginBottom: 24 }}>
                  <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12, color: '#166534' }}>Recommended Option</h2>
                  <p style={{ fontSize: 16, lineHeight: 1.6, color: '#14532d', whiteSpace: 'pre-wrap' }}>{decision.recommended_option}</p>
                </div>
              )}

              {/* Notes */}
              {decision.notes && (
                <div style={{ backgroundColor: '#f9fafb', padding: 24, borderRadius: 8, marginBottom: 24 }}>
                  <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>Notes</h2>
                  <p style={{ fontSize: 16, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{decision.notes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Bottom Action Bar */}
      {decisions.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'white',
          borderTop: '1px solid #e5e7eb',
          padding: '16px 24px',
          zIndex: 110
        }}>
          <div style={{ maxWidth: 1024, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {/* Navigation */}
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="outline" onClick={goToPrevious} disabled={currentIndex === 0}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <Button variant="outline" onClick={goToNext} disabled={currentIndex === decisions.length - 1}>
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 12 }}>
              <Button variant="outline" onClick={() => navigate(`/decisions/${decision?.decision_id}`)}>
                <MessageSquare className="w-4 h-4 mr-2" />
                View Details
              </Button>
              
              {canValidate() && (
                <>
                  <Button
                    variant="outline"
                    style={{ borderColor: '#f97316', color: '#ea580c' }}
                    onClick={() => openActionModal('Deferred')}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Defer
                  </Button>
                  <Button
                    style={{ backgroundColor: '#16a34a', color: 'white' }}
                    onClick={() => openActionModal('Validated')}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Validate
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom Modal (replacing Dialog component) */}
      {showActionModal && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 200
          }}
          onClick={closeActionModal}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: 8,
              padding: 24,
              width: '100%',
              maxWidth: 500,
              margin: 16
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: 20, fontWeight: 'bold', marginBottom: 8 }}>
              {actionType === 'Validated' ? 'Validate Decision' : 'Defer Decision'}
            </h2>
            <p style={{ color: '#6b7280', marginBottom: 16 }}>
              {actionType === 'Validated' 
                ? 'Mark this decision as validated by the TWG.'
                : 'Defer this decision for later review.'}
            </p>
            
            <div style={{ marginBottom: 16 }}>
              <Label style={{ display: 'block', marginBottom: 8 }}>Meeting Outcome Note</Label>
              <Textarea
                placeholder="Add notes about this decision..."
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                rows={4}
              />
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="outline" onClick={closeActionModal}>
                Cancel
              </Button>
              <Button
                onClick={handleQuickAction}
                disabled={submitting}
                style={{
                  backgroundColor: actionType === 'Validated' ? '#16a34a' : '#f97316',
                  color: 'white'
                }}
              >
                {submitting ? 'Processing...' : `Confirm ${actionType}`}
              </Button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default MeetingModePage;
